import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, eur } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Euro, Receipt, Store, CheckCircle2, AlertCircle, TrendingUp } from "lucide-react";

const Kpi = ({ icon: Icon, label, value, sub, testid }) => (
  <Card className="shadow-sm" data-testid={testid}>
    <CardContent className="p-6">
      <div className="flex items-center gap-2 text-muted-foreground text-sm">
        <Icon className="h-4 w-4" /> {label}
      </div>
      <div className="mt-2 font-heading text-3xl font-semibold tabular-nums tracking-tight">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </CardContent>
  </Card>
);

export default function Dashboard() {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data));
  }, []);

  if (!d) return <div className="text-muted-foreground">Laden…</div>;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">Overzicht van vandaag — {d.today}</p>
        </div>
        <Button asChild data-testid="dashboard-new-entry">
          <Link to="/journal">Nieuwe dagontvangst</Link>
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testid="kpi-today" icon={Euro} label="Ontvangsten vandaag (incl. BTW)" value={eur(d.today_total)} sub={`BTW: ${eur(d.today_vat)}`} />
        <Kpi testid="kpi-month" icon={TrendingUp} label="Deze maand" value={eur(d.month_total)} sub={`BTW: ${eur(d.month_vat)}`} />
        <Kpi testid="kpi-locations" icon={Store} label="Vestigingen" value={d.location_count} sub="actieve vestigingen" />
        <Kpi testid="kpi-recorded" icon={Receipt} label="Geboekt vandaag" value={`${d.locations_recorded}/${d.location_count}`} sub="vestigingen geregistreerd" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 shadow-sm">
          <CardHeader><CardTitle className="font-heading text-lg">Ontvangsten laatste 14 dagen</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={d.trend} margin={{ left: 8, right: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(214 32% 91%)" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} tick={{ fontSize: 11 }} stroke="hsl(215 16% 47%)" />
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(215 16% 47%)" width={48} />
                <Tooltip formatter={(v) => eur(v)} labelStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="total" stroke="hsl(221 83% 53%)" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader><CardTitle className="font-heading text-lg">Status vandaag per vestiging</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {d.location_status.length === 0 && <p className="text-sm text-muted-foreground">Nog geen vestigingen. Voeg er een toe.</p>}
            {d.location_status.map((l) => (
              <div key={l.id} className="flex items-center justify-between rounded-md border border-border px-3 py-2" data-testid={`loc-status-${l.id}`}>
                <div className="text-sm font-medium">{l.name}</div>
                {l.recorded ? (
                  <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 gap-1">
                    <CheckCircle2 className="h-3 w-3" /> {l.entry ? eur(l.entry.vat.total_gross) : "Geboekt"}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-amber-600 border-amber-300 gap-1">
                    <AlertCircle className="h-3 w-3" /> Nog te boeken
                  </Badge>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
